import { useParams, Link, Navigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { blogPosts } from '../data/blog';

// ✅ Direct imports from your bimages folder
import imgWebDev      from '../data/bimages/Codeviora-blog-1.webp';
import imgAppDev      from '../data/bimages/Codeviora-blog-2.webp';
import imgDesign      from '../data/bimages/Codeviora-blog-3.webp';
import imgSEO         from '../data/bimages/Codeviora-blog-4.webp';
import imgMarketing   from '../data/bimages/Codeviora-blog-5.webp';
import imgAI          from '../data/bimages/Codeviora-blog-6.webp';
import imgWordpress   from '../data/bimages/Codeviora-blog-7.webp';

// Keyed by unique imageKey — no more category collisions
const BLOG_IMAGES = {
  seo: imgSEO,
  appdev: imgAppDev,
  marketing: imgMarketing,
  design: imgDesign,
  webdev: imgWebDev,
  ai: imgAI,
  wordpress: imgWordpress,
};

const FALLBACK_IMAGE = imgWebDev;

function getPostImage(post) {
  return BLOG_IMAGES[post.imageKey] || BLOG_IMAGES[post.category] || FALLBACK_IMAGE;
}

// ── Breadcrumb ─────────────────────────────────────────────
function Breadcrumb({ category, title }) {
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-white/40">
      <ol className="flex flex-wrap items-center gap-2">
        <li>
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
        </li>
        <li aria-hidden="true">/</li>
        <li>
          <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
        </li>
        <li aria-hidden="true">/</li>
        <li>
          <Link
            to={`/blog?category=${encodeURIComponent(category)}`}
            className="hover:text-white transition-colors"
          >
            {category}
          </Link>
        </li>
        <li aria-hidden="true">/</li>
        <li className="text-white/70 truncate max-w-[220px] sm:max-w-none" aria-current="page">
          {title}
        </li>
      </ol>
    </nav>
  );
}

// ── Share Button Component ────────────────────────────────
function ShareButtons({ title, excerpt }) {
  const [copied, setCopied] = useState(false);
  const url = encodeURIComponent(window.location.href);
  const text = encodeURIComponent(`${title} — ${excerpt}`);

  const shares = [
    {
      name: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      color: 'hover:bg-[#0077b5] hover:border-[#0077b5]',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
    {
      name: 'Twitter / X',
      href: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      color: 'hover:bg-black hover:border-black',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    {
      name: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      color: 'hover:bg-[#1877f2] hover:border-[#1877f2]',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      name: 'WhatsApp',
      href: `https://wa.me/?text=${text}%20${url}`,
      color: 'hover:bg-[#25d366] hover:border-[#25d366]',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
    },
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-10 pt-8 border-t border-black/10 dark:border-white/10 reveal ">
      <p className="text-sm font-medium text-black/40 dark:text-white/40 mb-3">
        Share this article
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {shares.map((share) => {
          const { name, href, color, icon } = share;
          return (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${name}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-black/10 dark:border-white/10 text-black/50 dark:text-white/50 hover:text-white text-xs font-medium transition-all duration-200 ${color}`}
            >
              {icon}
              <span className="hidden sm:inline">{name}</span>
            </a>
          );
        })}
        {/* Copy Link */}
        <div className="relative">
          <button
            onClick={copyLink}
            aria-label="Copy link"
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-black/10 dark:border-white/10 text-black/50 dark:text-white/50 hover:bg-black hover:border-black hover:text-white text-xs font-medium transition-all duration-200"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
            </svg>
            <span className="hidden sm:inline">Copy Link</span>
          </button>
          {copied && (
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[11px] bg-black text-white px-2.5 py-1 rounded-md whitespace-nowrap animate-fade-up">
              Link copied!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Related Articles Carousel ─────────────────────────────
function RelatedCarousel({ posts }) {
  const trackRef = useRef(null);

  const scrollByCard = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector('[data-card]');
    const cardWidth = card ? card.offsetWidth + 24 : 300;
    track.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
  };

  if (posts.length === 0) return null;

  return (
    <div className="mt-14 pt-10 border-t border-black/10 dark:border-white/10 reveal ">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2
            className="text-2xl text-dark mb-1"
            style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
          >
            RELATED ARTICLES
          </h2>
          <p className="text-sm text-dark/40">Continue exploring more insights from CodeViora.</p>
        </div>
        <div className="hidden sm:flex items-center gap-2">
          <button
            onClick={() => scrollByCard(-1)}
            aria-label="Previous article"
            className="w-9 h-9 rounded-full border border-black/10 dark:border-white/10 flex items-center justify-center text-dark/50 hover:bg-black hover:text-white hover:border-black transition-all duration-200"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            onClick={() => scrollByCard(1)}
            aria-label="Next article"
            className="w-9 h-9 rounded-full border border-black/10 dark:border-white/10 flex items-center justify-center text-dark/50 hover:bg-black hover:text-white hover:border-black transition-all duration-200"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-2"
        style={{ scrollbarWidth: 'none' }}
      >
        {posts.map((post) => {
          const { slug, title, excerpt, category, readTime } = post;
          return (
            <Link
              key={slug}
              to={`/blog/${slug}`}
              data-card
              className="flex-shrink-0 w-[80%] sm:w-[46%] lg:w-[31%] card-black group overflow-hidden flex flex-col snap-start"
            >
              <div className="h-40 overflow-hidden rounded-t-2xl">
                <img
                  src={getPostImage(post)}
                  alt={title}
                  className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-white/50">{category}</span>
                  <span className="text-xs text-white/20">{readTime}</span>
                </div>
                <h3
                  className="text-white text-sm leading-snug group-hover:text-white/60 transition-colors mb-2"
                  style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                >
                  {title}
                </h3>
                <p className="text-xs text-white/30 leading-relaxed line-clamp-2 flex-1">{excerpt}</p>
                <span className="text-xs text-white/50 mt-4 inline-flex items-center gap-1">
                  Read Article →
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function BlogDetail() {
  useScrollReveal();
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  // Related: same category pehle, phir baaki — current post exclude
  const related = [
    ...blogPosts.filter(p => p.slug !== slug && p.category === post.category),
    ...blogPosts.filter(p => p.slug !== slug && p.category !== post.category),
  ].slice(0, 4);

  // Latest articles for sidebar — newest first, current post excluded
  const latest = blogPosts
    .filter(p => p.slug !== slug)
    .slice(0, 5);

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
      />

      {/* ── Section 1: Blog Introduction ───────────────────── */}
      <div className="bg-grid-black pt-16 pb-16 md:pt-20 md:pb-20 relative overflow-hidden border-white/10 rounded-b-[50px] md:rounded-b-[100px]">
       

        {/* Block 2 — Featured Image + Blog Information */}
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left — Featured Image */}
            <div className="overflow-hidden animate-fade-up">
               
               {/* Block 1 — Breadcrumb */}
               <div className="mb-4">
                <Breadcrumb category={post.category} title={post.title} />
              </div>
              
              <img
                src={getPostImage(post)}
                alt={post.title}
                className="rounded-2xl img-reveal w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                loading="eager"
              />
            </div>

            {/* Right — Blog Information */}
            <div className="animate-fade-up">
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="text-xs font-medium text-white bg-white/10 border border-white/10 px-3 py-1.5 rounded-full">
                  {post.category}
                </span>
                <span className="text-xs text-white/30">{post.readTime}</span>
                <span className="text-xs text-white/30">{post.date}</span>
              </div>
              <h1
                className="text-3xl md:text-4xl text-white leading-tight mb-5"
                style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
              >
                {post.title}
              </h1>
              <p className="text-lg text-white/50 leading-relaxed mb-8">{post.excerpt}</p>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white dark:bg-[#111] flex items-center justify-center text-black dark:text-white font-black text-sm">
                  CV
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{post.author.name}</p>
                  <p className="text-xs text-white/30">{post.author.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: Blog Content + Sidebar ──────────────── */}
      <section className="bg-grid-white py-14 md:py-20">
        <div className="container-max">
          <div className="grid lg:grid-cols-[75%_25%] gap-10 lg:gap-14 items-start">

            {/* Left Column — 75% */}
            <div className="min-w-0">
              <div className="max-w-3xl mx-auto lg:mx-0">
                <div className="prose-custom reveal" dangerouslySetInnerHTML={{ __html: post.content }} />

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-12 pt-10 border-t border-black/10 dark:border-white/10 reveal">
                  {post.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-xs font-medium text-black/50 dark:text-white/50 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 px-3 py-1.5 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Share Buttons */}
                <ShareButtons title={post.title} excerpt={post.excerpt} />

                {/* Related Articles Carousel */}
                <RelatedCarousel posts={related} />

                {/* CTA */}
                <div className="mt-14 bg-black rounded-2xl p-8 md:p-10 text-center reveal">
                  <h3
                    className="text-2xl text-white mb-2"
                    style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                  >
                    HAVE A PROJECT IN MIND?
                  </h3>
                  <p className="text-white/50 text-sm mb-5 max-w-lg mx-auto">
                    Whether you need a new website, a redesign, an eCommerce solution, or a custom digital experience, our team can help turn your ideas into something that works.
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link to="/contact" className="btn-primary-inv inline-flex">
                      Start a Project →
                    </Link>
                    <Link
                      to="/contact"
                      className="inline-flex items-center px-6 py-3 rounded-xl border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors"
                    >
                      Talk to Our Team
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column — Sidebar 25% */}
            <aside className="lg:sticky lg:top-24 flex flex-col gap-8">

              {/* WebToolsPark Promo Card */}
              <a
                href="https://webtoolspark.com/image-to-webp-converter/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black reveal block rounded-2xl border border-white/10 p-5 hover:border-white/30 hover:shadow-md transition-all duration-200"
              >
                <span className="text-[10px] font-medium text-white/70 bg-white/10 px-2 py-1 rounded-full">
                  WebToolsPark
                </span>

                <div className="flex items-center gap-3 mt-4 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="black"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-white leading-snug">
                      Need a Quick Image Conversion?
                    </p>
                  </div>
                </div>

                <p className="text-xs text-white/60 leading-relaxed mb-4">
                  Convert your images to WebP format quickly and easily with our free online
                  tool — JPG / PNG → WebP. Fast, simple and free.
                </p>

                <span className="text-xs font-medium text-white inline-flex items-center gap-1">
                  Try the Free Tool →
                </span>
              </a>

              {/* Latest Articles */}
              <div className="reveal">
                <h3
                  className="text-lg text-dark mb-5"
                  style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                >
                  LATEST ARTICLES
                </h3>
                <div className="flex flex-col gap-4">
                  {latest.map((lp) => {
                    const { slug: lslug, title, category, readTime, date } = lp;
                    return (
                      <Link
                        key={lslug}
                        to={`/blog/${lslug}`}
                        className="group flex items-center gap-3"
                      >
                        <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-xl">
                          <img
                            src={getPostImage(lp)}
                            alt={title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-dark/40 mb-1">{category} · {readTime || date}</p>
                          <p
                            className="text-sm text-dark leading-snug line-clamp-2 group-hover:text-dark/60 transition-colors"
                            style={{ fontFamily: 'Anton,sans-serif', fontWeight: 500 }}
                          >
                            {title}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
